import React, { useRef, useState } from 'react'
import Button from '../../../components/ui/Button'
import Icon from '../../../components/AppIcon'
import { auth } from '../../../firebase'

// Match the shop app's pipeline: downscale to <=1600px JPEG q85 before upload
// (faster on shop wifi, fewer vision tokens).
const MAX_IMAGE_SIDE = 1600
const JPEG_QUALITY = 0.85

function readFileAsDataURL(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = () => reject(new Error('Could not read the photo'))
    reader.readAsDataURL(file)
  })
}

function loadImage(dataUrl) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error('Could not decode the photo'))
    img.src = dataUrl
  })
}

async function downscaleToJpeg(file) {
  const original = await readFileAsDataURL(file)
  const img = await loadImage(original)
  const scale = Math.min(1, MAX_IMAGE_SIDE / Math.max(img.width, img.height))
  const width = Math.max(1, Math.round(img.width * scale))
  const height = Math.max(1, Math.round(img.height * scale))
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  canvas.getContext('2d').drawImage(img, 0, 0, width, height)
  return canvas.toDataURL('image/jpeg', JPEG_QUALITY)
}

/**
 * "Scan ID with AI" — photographs the customer's ID, sends it to the portal's
 * aiIdRead function (Claude vision) and hands the structured fields + the photo
 * back to the parent. On any failure the operator just types manually.
 */
const ScanIdButton = ({ onResult, onUnavailable }) => {
  const fileInputRef = useRef(null)
  const [scanning, setScanning] = useState(false)
  const [error, setError] = useState(null)

  const handleFile = async (e) => {
    const file = e.target.files?.[0]
    e.target.value = '' // allow rescanning the same file
    if (!file) return

    setScanning(true)
    setError(null)

    try {
      const photoDataUrl = await downscaleToJpeg(file)

      const token = await auth.currentUser?.getIdToken()
      const response = await fetch('/api/ai/id-read', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ imageBase64: photoDataUrl }),
      })
      const data = await response.json().catch(() => ({}))

      if (!response.ok) throw new Error(data?.error || `HTTP ${response.status}`)

      if (!data.available) {
        setError('AI is unavailable right now — please enter the ID manually.')
        if (onUnavailable) onUnavailable(data.reason)
        return
      }
      if (!data.success || !data.fields) {
        setError('Could not read this photo — try again with better lighting, or enter manually.')
        return
      }

      onResult(data.fields, photoDataUrl)
    } catch (err) {
      console.error('ID scan failed:', err)
      setError('Scan failed — please enter the ID manually.')
      if (onUnavailable) onUnavailable('unreachable')
    } finally {
      setScanning(false)
    }
  }

  return (
    <div className="space-y-2">
      <Button
        variant="tinted"
        size="lg"
        className="w-full"
        disabled={scanning}
        onClick={() => fileInputRef.current?.click()}
        iconName={scanning ? undefined : 'ScanLine'}
        iconPosition="left"
        loading={scanning}
      >
        {scanning ? 'Reading ID…' : 'Scan ID with AI'}
      </Button>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFile}
        className="hidden"
      />
      {error && (
        <p className="text-sm text-warning flex items-start gap-1.5">
          <Icon name="AlertTriangle" size={14} className="mt-0.5 flex-shrink-0" />
          {error}
        </p>
      )}
    </div>
  )
}

export default ScanIdButton
